import { computed, inject, InjectionToken, Signal } from '@angular/core';
import {
  BaseElement,
  ComponentElement,
  createElement,
  createIirdsRelationElement,
  HasIdentityDomainElement,
  HasIdentityElement,
  HasInformationTypeElement,
  HasRenditionElement,
  IdentityElement,
  InformationSubjectElement,
  InformationTypeElement,
  InformationUnitElement,
  RelatesToProductMetadataElement,
  RenditionElement,
} from '@enduradocs/iirds-parser';
import { tapResponse } from '@ngrx/operators';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import {
  FetchFile,
  SyncVirtualFile
} from '@rxap/fs';
import { Constructor, equals, noop, unique } from '@rxap/utilities';
import { CID } from 'multiformats';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  from,
  pipe,
  switchMap,
  tap,
} from 'rxjs';
import { PRODUCT_ID_IDENTITY_DOMAIN } from '../iirds-identity-domains';
import {
  FilterMatchMode,
  GroupedHit,
  SearchHit,
  SearchMode,
  SearchParams,
  SearchResponse,
} from './search-types';

export interface IirdsStore {
  informationUnits: Signal<InformationUnitElement[]>;
  documents: Signal<InformationUnitElement[]>;
  packages: Signal<InformationUnitElement[]>;
  informationTypes: Signal<InformationTypeElement[]>;
  informationSubjects: Signal<InformationSubjectElement[]>;
  components: Signal<ComponentElement[]>;
  documentTypes: Signal<InformationTypeElement[]>;
  cid: Signal<CID | null>;
  language: Signal<string | null>;
  hasCid: Signal<boolean>;
  hasLanguage: Signal<boolean>;
  hasBaseUrl: Signal<boolean>;
  baseUrl: Signal<string | null>;
  getBaseUrlForElement: (element: BaseElement) => string;
  getUrlForElement: (element: BaseElement, path: string) => Promise<string>;
  viewerUrl: (iu: InformationUnitElement) => string | null;
  ipfsGatewayDomain: Signal<string>;
  hasInformationUnit: (iri: string) => boolean;
  getContentBaseUrl: (productId: string) => string | null;
  addInformationUnit: (iu: InformationUnitElement) => void;
}

/**
 * Injection token with a compatible iirds store.
 */
export const IIRDS_STORE = new InjectionToken<IirdsStore>('IIRDS_STORE');

export function provideIirdsStore(store: Constructor<IirdsStore>) {
  return {
    provide: IIRDS_STORE,
    useExisting: store,
  };
}

export interface ISearchService {
  isReady: Signal<boolean>;
  hasError: Signal<boolean>;
  mode: Signal<SearchMode | null>;
  run: (params: SearchParams) => Promise<SearchResponse | null>;
  loadMoreGroupHits: (
    iri: string,
    pageIndex: number,
    pageSize: number,
    params: SearchParams
  ) => Promise<SearchHit[] | null>;
}

export const SEARCH_SERVICE = new InjectionToken<ISearchService>('SEARCH_SERVICE');

export function provideSearchService(service: Constructor<ISearchService>) {
  return {
    provide: SEARCH_SERVICE,
    useExisting: service,
  };
}

export enum FilterTypes {
  DOCUMENT_TYPE = 'documentTypes',
  INFORMATION_SUBJECT = 'informationSubjects',
  COMPONENT = 'components',
}

export const searchStore = signalStore(
  { providedIn: 'root' },
  withState<{
    value: string | null;
    result: SearchResponse | null;
    limit: number;
    executing: boolean;
    informationTypes: string[];
    components: string[];
    documentTypes: string[];
    informationSubjects: string[];
    informationUnits: string[];
    groupBy: string[];
    selectedFilterTypes: FilterTypes[];
    filterMatchMode: FilterMatchMode;
  }>({
    value: null,
    result: null,
    limit: 42,
    executing: false,
    informationTypes: [],
    components: [],
    documentTypes: [],
    informationSubjects: [],
    informationUnits: [],
    groupBy: [],
    selectedFilterTypes: [],
    filterMatchMode: 'all',
  }),
  withComputed((state) => {
    const iirds = inject(IIRDS_STORE);
    const searchService = inject(SEARCH_SERVICE);
    const filter = computed(() => ({
      documentTypes: state.documentTypes(),
      informationTypes: state.informationTypes(),
      informationSubjects: state.informationSubjects(),
      informationUnits: state.informationUnits(),
      components: state.components(),
    }));
    return {
      activeFilterTypes: computed(() => {
        const active: FilterTypes[] = state.selectedFilterTypes();
        const filters = filter();
        return active
          .concat(Object.entries(filters).filter(([, value]) => value.length).map(([key]) => key as FilterTypes))
          .filter(unique());
      }),
      filter,
      filteredInformationUnits: computed(() => {
        const {
          components,
          documentTypes,
          informationSubjects,
          informationTypes,
          informationUnits,
        } = filter();
        const matchMode = state.filterMatchMode();

        let iuList = ([] as InformationUnitElement[]).concat(
          iirds.packages(),
          iirds.documents()
        );

        if (
          components.length ||
          documentTypes.length ||
          informationSubjects.length ||
          informationTypes.length ||
          informationUnits.length
        ) {
          iuList =
            iirds.informationUnits().filter((iu) => {
              // Collect one result per *active* category (a category with no
              // selection is skipped). Within a category values are OR'd via
              // `.some()`. The active categories are then combined with `every`
              // ("match all") or `some` ("match any") depending on the mode.
              // Skipping empty categories is essential in "any" mode — an empty
              // category must not contribute a `true` that matches everything.
              const checks: boolean[] = [];
              if (informationSubjects.length) {
                checks.push(
                  iu.subjectList.some(
                    (s: InformationSubjectElement) =>
                      s.about && informationSubjects.includes(s.about)
                  )
                );
              }
              if (components.length) {
                checks.push(
                  iu.componentList.some(
                    (c: ComponentElement) =>
                      c.about && components.includes(c.about)
                  )
                );
              }
              if (documentTypes.length) {
                checks.push(
                  iu.informationTypeList.some(
                    (c: InformationTypeElement) =>
                      c.about && documentTypes.includes(c.about)
                  )
                );
              }
              if (informationTypes.length) {
                checks.push(
                  iu.informationTypeList.some(
                    (c: InformationTypeElement) =>
                      c.about && informationTypes.includes(c.about)
                  )
                );
              }
              if (informationUnits.length) {
                checks.push(informationUnits.includes(iu.about));
              }

              return matchMode === 'any'
                ? checks.some(Boolean)
                : checks.every(Boolean);
            }) ?? [];
        }

        return iuList;
      }),
      informationTypeOptions: computed(() =>
        iirds
          .informationTypes()
          .map((it) => ({ display: it.getLabel().value, value: it.about }))
      ),
      documentTypeOptions: computed(() =>
        iirds
          .documentTypes()
          .map((dt) => ({ display: dt.getLabel().value, value: dt.about }))
      ),
      informationSubjectOptions: computed(() =>
        iirds
          .informationSubjects()
          .map((is) => ({ display: is.getLabel().value, value: is.about }))
      ),
      componentOptions: computed(() =>
        iirds
          .components()
          .map((c) => ({ display: c.getLabel().value, value: c.about }))
      ),
      isReady: computed(() => searchService.isReady()),
      hasError: computed(() => searchService.hasError()),
      mode: computed(() => searchService.mode()),
      params: computed<SearchParams>(() => ({
        query: state.value()?.trim().replace(/\s+/g, ' ') ?? null,
        limit: state.limit(),
        cid: iirds.hasCid() ? iirds.cid()?.toV1().toString() : undefined,
        language: iirds.language(),
        ...filter(),
        filterMatchMode: state.filterMatchMode(),
        groupBy: state.groupBy(),
      })),
    };
  }),
  withMethods((state) => {
    const searchService = inject(SEARCH_SERVICE);
    const iirds = inject(IIRDS_STORE);
    const startExecuting = () => {
      patchState(state, { executing: true });
    };
    const stopExecuting = () => {
      patchState(state, { executing: false });
    };
    const extractInformationUnits = (groupHit: GroupedHit) => {
      const ipfsGatewayDomain = iirds.ipfsGatewayDomain();
      for (const document of groupHit.hits
        .map((hit) => hit.document)
        .filter(unique(['iri']))) {
        const iri = document.id.split('@')[0];
        if (iirds.hasInformationUnit(iri)) {
          continue;
        }
        const iu = createElement(InformationUnitElement, {
          about: iri,
          title: document.title,
          hasRenditionList:
            document.renditions?.map((r) =>
              createIirdsRelationElement(HasRenditionElement, RenditionElement, {
                format: r.format,
                source: r.source,
              })
            ) ?? [],
          hasIdentityList:
            document.identities?.map((i) =>
              createIirdsRelationElement(HasIdentityElement, IdentityElement, {
                identifier: i.identifier,
                hasIdentityDomain: createIirdsRelationElement(
                  HasIdentityDomainElement,
                  i.domain
                ),
              })
            ) ?? [],
          hasInformationTypeList:
            document.informationTypes?.map((iri) =>
              createIirdsRelationElement(HasInformationTypeElement, iri)
            ) ?? [],
          relatesToProductMetadata:
            document.informationTypes?.map((iri) =>
              createIirdsRelationElement(RelatesToProductMetadataElement, iri)
            ) ?? [],
        });
        if (document.cid) {
          iu.setCid(document.cid);
        }
        iu.setIpfsGatewayDomain(ipfsGatewayDomain);
        const contentBaseUrl = iirds.getContentBaseUrl(document.productId);
        if (contentBaseUrl) {
          iu.setBaseUrl(contentBaseUrl);
        }
        const contentRendition = iu.renditionList.find(
          (r) => r.format === document.format && r.source === document.source
        );
        if (contentRendition) {
          contentRendition.setFile(
            new SyncVirtualFile(
              contentRendition.source.split('/').pop()!,
              contentRendition.source,
              new TextEncoder().encode(document.content),
              contentRendition.format,
              TextDecoder,
              TextEncoder
            )
          );
        }
        for (const rendition of iu.renditionList.filter(
          (r) => r !== contentRendition
        )) {
          rendition.setFile(
            new FetchFile(
              rendition.source,
              `${iirds.getBaseUrlForElement(rendition)}/${rendition.source}`,
              rendition.format
            )
          );
        }
        iu.addIdentity(PRODUCT_ID_IDENTITY_DOMAIN, document.productId);
        iirds.addInformationUnit(iu);
      }
    }
    const setResult = (result: SearchResponse | null) => {
      result?.groupedHits.forEach(groupHit => extractInformationUnits(groupHit));
      patchState(state, { result });
    };
    const updateGroupHits = (iri: string, newHits: SearchHit[]) => {
      const currentResult = state.result();
      if (!currentResult || !currentResult.groupedHits) {
        return;
      }

      // Find the group and update its hits
      const updatedGroupedHits = currentResult.groupedHits.map((group) => {
        if (group.groupKey === iri) {
          return {
            ...group,
            hits: group.hits.concat(newHits),
          };
        }
        return group;
      });

      updatedGroupedHits.forEach(groupHit => extractInformationUnits(groupHit));

      patchState(state, {
        result: {
          ...currentResult,
          groupedHits: updatedGroupedHits,
        },
      });
    };
    return {
      extractInformationUnits,
      toggleSelectedFilter: (type: FilterTypes) => {
        if (state.selectedFilterTypes().includes(type)) {
          patchState(state, {
            selectedFilterTypes: state
              .selectedFilterTypes()
              .filter((filterType) => filterType !== type),
          });
        } else {
          patchState(state, {
            selectedFilterTypes: [...state.selectedFilterTypes(), type],
          });
        }
      },
      search: rxMethod<SearchParams>(
        pipe(
          debounceTime(500),
          tapResponse({
            next: (params) => {
              if (!params.query) {
                setResult(null);
              }
            },
            error: noop,
          }),
          filter(
            (params) => params.query !== null && params.query.trim() !== ''
          ),
          distinctUntilChanged((prev, next) => equals(prev, next)),
          tap(() => startExecuting()),
          switchMap((params) =>
            from(searchService.run(params)).pipe(
              tapResponse({
                next: (results) => {
                  setResult(results);
                },
                error: (err: Error) => {
                  setResult(null);
                  console.error('Error during search:', err);
                },
                finalize: () => {
                  stopExecuting();
                },
              })
            )
          )
        )
      ),
      async loadMoreGroupHits(
        iri: string,
        pageIndex: number,
        pageSize: number,
        params: SearchParams = state.params()
      ) {
        startExecuting();
        try {
          const result = await searchService.loadMoreGroupHits(
            iri,
            pageIndex,
            pageSize,
            params
          );
          if (result?.length) {
            updateGroupHits(iri, result);
          }
        } catch (error: any) {
          console.error('Error during search:', error);
        } finally {
          stopExecuting();
        }
      },
      clear() {
        patchState(state, {
          result: null,
          value: null,
        });
      },
      setValue(value: string | null) {
        patchState(state, { value });
      },
      setInformationTypes(informationTypes: string[]) {
        patchState(state, { informationTypes });
      },
      setInformationUnits(informationUnits: string[]) {
        patchState(state, { informationUnits });
      },
      setInformationSubjects(informationSubjects: string[]) {
        patchState(state, { informationSubjects });
      },
      setComponents(components: string[]) {
        patchState(state, { components });
      },
      setDocumentTypes(documentTypes: string[]) {
        patchState(state, { documentTypes });
      },
      setGroupBy(groupBy: string[]) {
        patchState(state, { groupBy });
      },
      setFilterMatchMode(filterMatchMode: FilterMatchMode) {
        patchState(state, { filterMatchMode });
      },
      /**
       * Clears every active filter category (but keeps the selected match mode).
       * Backs the "Clear all" action in the collapsed filter summary.
       */
      clearFilters() {
        patchState(state, {
          informationTypes: [],
          components: [],
          documentTypes: [],
          informationSubjects: [],
          informationUnits: [],
          selectedFilterTypes: [],
        });
      },
      setLimit(limit: number) {
        patchState(state, { limit });
      }
    };
  }),
  withHooks({
    onInit(state) {
      state.search(state.params);
    },
  })
);
